import React, { useState } from 'react';
import { Alert, Button, Container } from 'reactstrap';
import { ProfileCard } from './ProfileCard';
import { AddProfileModal } from './AddProfileModal';

type ProfilesSectionProperties =
{
    connection: any | null;
    profiles: any[];
    onCreateProfile: (profile: any) => Promise<any>
    onUpdateProfile: (profileId: string, profile: any) => Promise<any>
    onDeleteProfile: (profileId: string) => Promise<any>
    onCreateProfilesForActiveLanguages: () => Promise<any>
    onCreateDataSourceForProfile: (profileId: string) => Promise<any>
}

function ProfilesSection
(
    {
        connection,
        profiles,
        onCreateProfile,
        onUpdateProfile,
        onDeleteProfile,
        onCreateProfilesForActiveLanguages,
        onCreateDataSourceForProfile
    }
    : ProfilesSectionProperties
)
{
    const [isAddProfileModalOpen, setIsAddProfileModalOpen] = useState(false);
    const [isCreatingFromLanguages, setIsCreatingFromLanguages] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const handleCreateProfile = async (profile: any) =>
    {
        setMessage(null);
        setError(null);

        try
        {
            await onCreateProfile(profile);
            setMessage('Profil został dodany.');
        }
        catch (exception: any)
        {
            setError(exception?.message ?? 'Tworzenie profilu zakończyło się błędem.');
            throw exception;
        }
    };

    const handleCreateProfilesForActiveLanguages = async () =>
    {
        setMessage(null);
        setError(null);
        setIsCreatingFromLanguages(true);

        try
        {
            await onCreateProfilesForActiveLanguages();
            setMessage('Profile z aktywnych języków zostały utworzone lub zaktualizowane.');
        }
        catch (exception: any)
        {
            setError(exception?.message ?? 'Tworzenie profili na podstawie aktywnych języków zakończyło się błędem.');
        }
        finally
        {
            setIsCreatingFromLanguages(false);
        }
    };

    return (
        <Container fluid>
            {message && <Alert color="success">{message}</Alert>}
            {error && <Alert color="danger">{error}</Alert>}

            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mb-3">
                <div>
                    <h5 className="mb-1">Profile eksportu</h5>
                    <div className="text-muted">
                        Jeden profil odpowiada jednej wersji eksportu: język, feed label, waluta i źródło danych.
                    </div>
                </div>
                <div className="mt-3 mt-lg-0">
                    <Button
                        type="button"
                        color="secondary"
                        className="mr-2"
                        disabled={isCreatingFromLanguages}
                        onClick={handleCreateProfilesForActiveLanguages}
                    >
                        Utwórz z aktywnych języków
                    </Button>

                    <Button
                        type="button"
                        color="secondary"
                        onClick={() => setIsAddProfileModalOpen(true)}
                    >
                        Dodaj profil
                    </Button>
                </div>
            </div>

            <AddProfileModal
                isOpen={isAddProfileModalOpen}
                setIsOpen={setIsAddProfileModalOpen}
                onConfirm={handleCreateProfile}
                nextIndex={profiles.length}
            />

            {profiles.length === 0 && (
                <Alert color="warning">
                    Brak profili eksportu.
                </Alert>
            )}

            {profiles.map((profile: any) => (
                <ProfileCard
                    key={`${profile.profileId}-${profile.dataSourceName || ''}-${profile.sortOrder}`}
                    profile={profile}
                    merchantAccountId={connection?.merchantAccountId}
                    onUpdateProfile={onUpdateProfile}
                    onDeleteProfile={onDeleteProfile}
                    onCreateDataSourceForProfile={onCreateDataSourceForProfile}
                />
            ))}
        </Container>
    );
}

export default ProfilesSection;